/**
 * A1 · ভাষা নির্বাচন — `/welcome/language`
 * Frame: `Oprappo P0 Screens.dc.html`, "A1 · ভাষা নির্বাচন".
 *
 * Frame note: "Each language is written in itself, with its English name beneath.
 * Bangla preselected, so most users tap once." The preselection is the drawn state,
 * so `bn` is the initial value and the frame is what renders on load.
 *
 * ── W2-12 · ONE LANGUAGE, REMEMBERED ─────────────────────────────────────────
 *
 * The frame draws two rows, বাংলা and English. There is no English rendering of this
 * product — every screen is Bangla, and no English copy exists to switch to — so the
 * English row offered a choice that changed nothing. Founder decision 2026-09-18:
 * "English UI is hidden until English copy exists; the product stays Bangla-first."
 * The row is gone (recorded in `docs/deviations/W2-12.md`, A1), and comes back as one
 * entry in `LANGUAGES` below on the day English copy ships.
 *
 * The choice is now KEPT: শুরু করুন writes it to this device before moving on, so the
 * next screen — and every later visit — can read what the person chose rather than
 * assume it. `localStorage`, wrapped, because Safari's private mode throws on a write and
 * a person in a private window must still get past A1.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import { Frame, SAND_FRAME, Mark, Radio } from './frame.js';

export type Lang = 'bn';

/** The languages this build can render. English joins when its copy exists. */
export const LANGUAGES: ReadonlyArray<{ id: Lang; native: string; english: string }> = [
  { id: 'bn', native: 'বাংলা', english: 'Bangla' },
];

/** Where the choice is kept on this device. */
export const LANGUAGE_KEY = 'oprappo.language.v1';

/** Keep the choice. Never throws: an unusable store is a device that forgets. */
export function rememberLanguage(lang: Lang): void {
  try {
    globalThis.localStorage?.setItem(LANGUAGE_KEY, lang);
  } catch {
    /* private mode, blocked storage: the choice holds for this visit only */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
}

/** The kept choice, or `null` when this device has none — or none this build can render. */
export function storedLanguage(): Lang | null {
  try {
    const held = globalThis.localStorage?.getItem(LANGUAGE_KEY);
    return LANGUAGES.some((l) => l.id === held) ? (held as Lang) : null;
  } catch {
    return null;
  }
}

export function LanguageScreen(): JSX.Element {
  const [lang, setLang] = useState<Lang>(() => storedLanguage() ?? 'bn');
  const navigate = useNavigate();

  const start = (): void => {
    rememberLanguage(lang);
    navigate('/welcome');
  };

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[32px] pb-[24px]`}>
      <Mark size={44} />

      <div className="mt-[40px]">
        <h1 className="m-0 font-anek font-semibold text-[26px] leading-[1.25] text-ink">
          ভাষা বেছে নিন
        </h1>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]">
          পরে অ্যাকাউন্ট থেকে বদলাতে পারবেন।
        </div>
      </div>

      <div className="mt-[32px] flex flex-col gap-[11px]">
        {LANGUAGES.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={lang === option.id}
            onClick={() => setLang(option.id)}
            className={
              'bg-white h-[60px] flex items-center justify-between px-[17px] text-left rounded-none ' +
              (lang === option.id ? 'border-[1.5px] border-teal' : 'border border-border')
            }
          >
            <span className="block">
              <span className="block font-anek font-semibold text-[19px] leading-[1.75] text-ink">
                {option.native}
              </span>
              <span className="block font-manrope font-normal text-[13px] leading-[1.3] text-muted mt-[2px]">
                {option.english}
              </span>
            </span>
            <Radio selected={lang === option.id} />
          </button>
        ))}
      </div>

      <div className="flex-1" />

      <Button tone="primary" size="lg" onClick={start}>
        শুরু করুন
      </Button>
    </Frame>
  );
}
